import React, { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  GetUserAgentChats,
  GetUserAgentConnections,
} from "../../Apis_chatbot/apis";

import { ChatRoles } from "../../app/Enums/ChatRoles";
import "./ChatCommon.css";
import "./User_Agent1.css";
import Pagination from "../../app/components/Pagination/Pagination";
import MarkdownMessage from "../../app/components/MarkdownMessage/MarkdownMessage";

import { toast } from "react-toastify";

// Human-readable role name from the shared ChatRoles enum.
const getRoleName = (roleId) => ChatRoles[roleId] || "Unknown";

// First letter for a monogram avatar.
const getInitial = (value) => {
  const text = (value || "").trim();
  return text ? text[0].toUpperCase() : "?";
};

const sameCalendarDay = (a, b) =>
  new Date(a).toDateString() === new Date(b).toDateString();

const formatDayLabel = (value) =>
  new Date(value).toLocaleDateString(undefined, {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const formatTime = (value) =>
  new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

export default function UserAgentConversation() {
  const [connections, setConnections] = useState([]);
  const [chats, setChats] = useState([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState(null);

  // Loaders for the conversation list and the message list.
  const [connectionsLoading, setConnectionsLoading] = useState(true);
  const [chatsLoading, setChatsLoading] = useState(false);

  const chatEndRef = useRef(null);
  const tableRef = useRef(null);

  // ============================================
  // Pagination states
  // ============================================
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // ============================================
  // Scroll chat to bottom whenever chats change
  // ============================================
  useEffect(() => {
    scrollToBottom();
  }, [chats]);

  const scrollToBottom = () => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  // ============================================
  // Fetch conversations
  // ============================================
  useEffect(() => {
    fetchConnections();
  }, [currentPage, pageSize]);

  const fetchConnections = async () => {
    setConnectionsLoading(true);

    try {
      const response = await GetUserAgentConnections({
        pageNo: currentPage,
        pageSize: pageSize,
      });

      setConnections(response.result_value || []);

      if (response.total_row) {
        setTotalPages(Math.ceil(response.total_row / pageSize));
      } else {
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching connections:", error);

      setConnections([]);
      toast.error("Unable to load conversations");
    } finally {
      setConnectionsLoading(false);
    }
  };

  // ============================================
  // Fetch chats for selected conversation
  // ============================================
  const fetchChats = async (connectionId) => {
    setChatsLoading(true);

    try {
      const response = await GetUserAgentChats({
        connectionId: connectionId,
      });

      if (response) {
        setChats(response.result_value || []);
      } else {
        setChats([]);
      }
    } catch (error) {
      console.error("Error fetching chat:", error);

      toast.error("Could not load chat messages");

      setChats([]);
    } finally {
      setChatsLoading(false);
    }
  };

  // ============================================
  // Format date
  // ============================================
  const formatDate = (dateString) => {
    if (!dateString) {
      return "Ongoing";
    }

    return new Date(dateString).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ============================================
  // Select conversation
  // ============================================
  const handleSelectConnection = (connectionId) => {
    setSelectedConnectionId(connectionId);
    setChats([]);

    fetchChats(connectionId);
  };

  // ============================================
  // Selected conversation
  // ============================================
  const selectedConnection = connections.find(
    (connection) => connection.id === selectedConnectionId
  );

  const headerText =
    selectedConnection?.userName?.trim() ||
    selectedConnection?.connectionId ||
    "No conversation selected";

  const hasSelection = Boolean(selectedConnectionId);

  const headerSubtitle = !hasSelection
    ? "Pick a conversation from the list"
    : selectedConnection
      ? `Connection ${selectedConnection.connectionId} · agent ${
          selectedConnection.agentName || "—"
        }`
      : "Loading conversation";

  // ============================================
  // Scroll sidebar to top when pagination changes
  // ============================================
  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [pageSize, currentPage]);

  // ============================================
  // Render
  // ============================================
  return (
    <div className="uc-page">
      {/* HEADER */}
      <header className="uc-header">
        <div className="uc-header-titles">
          <h1>User &ndash; Agent Conversations</h1>
          <p>Review live-agent handover conversations between users and agents.</p>
        </div>

        {!connectionsLoading && connections.length > 0 && (
          <div className="uc-header-stat">
            {connections.length}{" "}
            {connections.length === 1 ? "conversation" : "conversations"}
          </div>
        )}
      </header>

      {/* WORKSPACE */}
      <div className="uc-shell">
        {/* SIDEBAR — connections */}
        <aside className="uc-sidebar">
          <div className="uc-sidebar-head">
            <h2>Conversations</h2>
            {!connectionsLoading && connections.length > 0 && (
              <span className="uc-count">{connections.length} shown</span>
            )}
          </div>

          <div className="uc-sidebar-body" ref={tableRef}>
            {connectionsLoading ? (
              <div className="uc-inline-loader">
                <Loader2 className="uc-spin" size={20} />
                <span>Loading conversations&hellip;</span>
              </div>
            ) : connections.length === 0 ? (
              <div className="uc-list-empty">No conversations found</div>
            ) : (
              connections.map((conn) => {
                const isLive = !conn.endedAt;

                return (
                  <div
                    key={conn.id}
                    role="button"
                    tabIndex={0}
                    className={`uc-conv ${
                      selectedConnectionId === conn.id ? "uc-conv--active" : ""
                    }`}
                    onClick={() => handleSelectConnection(conn.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSelectConnection(conn.id);
                      }
                    }}
                  >
                    <div className="uc-conv-top">
                      <span className="uc-avatar uc-avatar--agent">
                        {getInitial(conn.userName)}
                      </span>

                      <span className="uc-conv-ident">
                        <strong>{conn.userName || "Anonymous user"}</strong>
                        <span>Agent: {conn.agentName || "Unassigned"}</span>
                      </span>

                      <span
                        className={`uc-conn-status ${
                          isLive ? "uc-conn-status--live" : "uc-conn-status--ended"
                        }`}
                      >
                        {isLive ? "Live" : "Ended"}
                      </span>
                    </div>

                    <dl className="uc-conv-meta">
                      <div>
                        <dt>Connection</dt>
                        <dd title={String(conn.connectionId ?? "—")}>
                          {conn.connectionId ?? "—"}
                        </dd>
                      </div>
                      <div>
                        <dt>Started</dt>
                        <dd title={formatDate(conn.startedAt)}>
                          {formatDate(conn.startedAt)}
                        </dd>
                      </div>
                      <div>
                        <dt>Ended</dt>
                        <dd title={formatDate(conn.endedAt)}>
                          {formatDate(conn.endedAt)}
                        </dd>
                      </div>
                    </dl>
                  </div>
                );
              })
            )}
          </div>

          {totalPages > 1 && (
            <div className="uc-sidebar-foot">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={(page) => {
                  setCurrentPage(page);
                  setSelectedConnectionId(null);
                  setChats([]);
                }}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                  setSelectedConnectionId(null);
                  setChats([]);
                }}
              />
            </div>
          )}
        </aside>

        {/* CHAT PANEL */}
        <section className="uc-chat">
          <div className="uc-chat-head">
            <span
              className={`uc-avatar uc-avatar--agent ${hasSelection ? "" : "uc-avatar--bot"}`}
            >
              {hasSelection ? getInitial(headerText) : "—"}
            </span>

            <div className="uc-chat-head-text">
              <strong title={headerText}>{headerText}</strong>
              <span title={headerSubtitle}>{headerSubtitle}</span>
            </div>

            {hasSelection && !chatsLoading && chats.length > 0 && (
              <span className="uc-chat-head-badge">
                {chats.length} {chats.length === 1 ? "message" : "messages"}
              </span>
            )}
          </div>

          <div className="uc-chat-body">
            <div className="uc-messages">
              {!hasSelection ? (
                <div className="uc-state">
                  <div className="uc-state-icon" aria-hidden="true">
                    💬
                  </div>
                  <h3>No conversation selected</h3>
                  <p>Select a conversation from the list to view its messages.</p>
                </div>
              ) : chatsLoading ? (
                <div className="uc-inline-loader">
                  <Loader2 className="uc-spin" size={22} />
                  <p>Loading conversation&hellip;</p>
                </div>
              ) : chats.length === 0 ? (
                <div className="uc-state">
                  <div className="uc-state-icon" aria-hidden="true">
                    📭
                  </div>
                  <h3>No messages</h3>
                  <p>There are no messages in this conversation yet.</p>
                </div>
              ) : (
                chats.map((chat, idx) => {
                  const roleId = chat.senderRoleId;
                  const variant = roleId === ChatRoles.Agent ? "agent" : "user";

                  const prev = chats[idx - 1];
                  const showDivider =
                    chat.sendedAt &&
                    (idx === 0 ||
                      !prev?.sendedAt ||
                      !sameCalendarDay(prev.sendedAt, chat.sendedAt));

                  return (
                    <React.Fragment key={idx}>
                      {showDivider && (
                        <div className="uc-day-divider">
                          {formatDayLabel(chat.sendedAt)}
                        </div>
                      )}

                      <div className={`uc-row uc-row--${variant}`}>
                        <div className={`uc-bubble uc-bubble--${variant}`}>
                          <div className="uc-bubble-top">
                            <span className="uc-bubble-role">
                              {getRoleName(roleId)}
                            </span>
                            <span className="uc-bubble-time">
                              {chat.sendedAt ? formatTime(chat.sendedAt) : ""}
                            </span>
                          </div>
                          <div className="uc-bubble-text">
                            <MarkdownMessage text={chat.message} />
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })
              )}

              {/* Scroll anchor */}
              <div ref={chatEndRef}></div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
